import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  createProperty,
  updateProperty,
  getHostPropertyDetail,
  uploadPropertyImage,
  deletePropertyImage,
  publishProperty,
  getCities,
  getAmenities,
  getHostProfile
} from '../../lib/api';
import { toast } from 'sonner';
import { 
  Loader2, 
  Upload, 
  X, 
  Camera, 
  CheckCircle2, 
  AlertCircle,
  DollarSign,
  Calendar,
  Shield,
  ExternalLink,
  Image as ImageIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PROPERTY_TYPES = [
  { value: 'house', label: 'House' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'villa', label: 'Villa' },
  { value: 'cottage', label: 'Cottage' },
  { value: 'apartment', label: 'Apartment' },
];

const CANCELLATION_POLICIES = [
  { value: 'flexible', label: 'Flexible - Full refund up to 24 hours before' },
  { value: 'moderate', label: 'Moderate - Full refund up to 5 days before' },
  { value: 'strict', label: 'Strict - 50% refund up to 14 days before' },
];

// Listing Readiness Checklist Component
function ListingReadinessChecklist({ form, images, hostProfile }) {
  const checks = [
    {
      id: 'photos',
      label: 'Photos uploaded',
      description: 'At least 1 photo required',
      complete: images.length > 0,
      required: true
    },
    {
      id: 'pricing',
      label: 'Pricing set',
      description: 'Base nightly rate configured',
      complete: form.base_price_nightly > 0,
      required: true
    },
    {
      id: 'policies',
      label: 'Policies configured',
      description: 'Cancellation policy selected',
      complete: !!form.cancellation_policy,
      required: true
    },
    {
      id: 'payouts',
      label: 'Payout account connected',
      description: 'Stripe Connect for receiving payments',
      complete: hostProfile?.stripe_account_id || hostProfile?.payout_enabled,
      required: false
    }
  ];

  const completedCount = checks.filter(c => c.complete).length;
  const allRequiredComplete = checks.filter(c => c.required).every(c => c.complete);

  return (
    <Card className="border-2 border-dashed border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-primary" />
            Listing Readiness
          </CardTitle>
          <span className="text-sm text-slate-500">
            {completedCount}/{checks.length} complete
          </span>
        </div>
        <CardDescription>
          {allRequiredComplete 
            ? 'Your listing is ready to publish!' 
            : 'Complete these items for the best results'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {checks.map((check) => (
          <div 
            key={check.id}
            className={`flex items-start gap-3 p-3 rounded-lg ${
              check.complete ? 'bg-green-50' : 'bg-slate-50'
            }`}
          >
            {check.complete ? (
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
            ) : (
              <AlertCircle className={`w-5 h-5 mt-0.5 ${check.required ? 'text-amber-500' : 'text-slate-400'}`} />
            )}
            <div className="flex-1">
              <p className={`text-sm font-medium ${check.complete ? 'text-green-800' : 'text-slate-700'}`}>
                {check.label}
                {!check.required && <span className="text-slate-400 font-normal ml-1">(recommended)</span>}
              </p>
              <p className="text-xs text-slate-500">{check.description}</p>
            </div>
          </div>
        ))}
        
        {!allRequiredComplete && (
          <p className="text-xs text-amber-600 flex items-center gap-1 mt-2">
            <AlertCircle className="w-3 h-3" />
            Complete required items before publishing
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function HostPropertyForm() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!propertyId;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [cities, setCities] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [hostProfile, setHostProfile] = useState(null);

  const [form, setForm] = useState({
    city_id: '',
    title: '',
    description: '',
    property_type: 'house',
    beds: 1,
    baths: 1,
    max_guests: 2,
    base_price_nightly: 10000, // $100 in cents
    weekend_price_nightly: null, // null = same as base
    cleaning_fee: 5000, // $50 in cents
    min_nights: 1,
    max_nights: 30,
    instant_book_enabled: false,
    cancellation_policy: 'moderate',
    amenities: [],
    address_line1: '',
    address_line2: '',
    postal_code: '',
    // Discounts
    weekly_discount_enabled: false,
    weekly_discount_percent: 10,
    monthly_discount_enabled: false,
    monthly_discount_percent: 20,
    // Safety disclosures
    has_security_cameras: false,
    has_noise_monitors: false
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        const [citiesRes, amenitiesRes, profileRes] = await Promise.all([
          getCities(),
          getAmenities(),
          getHostProfile()
        ]);
        setCities(citiesRes.data);
        setAmenities(amenitiesRes.data);
        setHostProfile(profileRes.data);

        if (isEditing) {
          const propertyRes = await getHostPropertyDetail(propertyId);
          const property = propertyRes.data;
          setForm({
            city_id: property.city_id,
            title: property.title,
            description: property.description,
            property_type: property.property_type,
            beds: property.beds,
            baths: property.baths,
            max_guests: property.max_guests,
            base_price_nightly: property.base_price_nightly,
            weekend_price_nightly: property.weekend_price_nightly || null,
            cleaning_fee: property.cleaning_fee || 0,
            min_nights: property.min_nights,
            max_nights: property.max_nights,
            instant_book_enabled: property.instant_book_enabled,
            cancellation_policy: property.cancellation_policy,
            amenities: property.amenities || [],
            address_line1: property.address_line1 || '',
            address_line2: property.address_line2 || '',
            postal_code: property.postal_code || '',
            // Discounts
            weekly_discount_enabled: property.weekly_discount_enabled || false,
            weekly_discount_percent: property.weekly_discount_percent || 10,
            monthly_discount_enabled: property.monthly_discount_enabled || false,
            monthly_discount_percent: property.monthly_discount_percent || 20,
            // Safety disclosures
            has_security_cameras: property.has_security_cameras || false,
            has_noise_monitors: property.has_noise_monitors || false
          });
          setImages(property.images || []);
        }
      } catch (error) {
        console.error('Failed to load data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [propertyId, isEditing]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAmenityToggle = (amenityId) => {
    setForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenityId)
        ? prev.amenities.filter(a => a !== amenityId)
        : [...prev.amenities, amenityId]
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !propertyId) return;

    setUploading(true);
    try {
      for (const file of files) {
        const response = await uploadPropertyImage(propertyId, file, images.length === 0);
        setImages(prev => [...prev, response.data]);
      }
      toast.success('Images uploaded successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleImageDelete = async (imageId) => {
    try {
      await deletePropertyImage(propertyId, imageId);
      setImages(prev => prev.filter(img => img.id !== imageId));
      toast.success('Image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.city_id) {
      toast.error('Please select a city');
      return;
    }
    if (!form.title || form.title.length < 5) {
      toast.error('Title must be at least 5 characters');
      return;
    }
    if (!form.description || form.description.length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }

    setSaving(true);
    try {
      // Prepare data - convert weekend price to null if not set
      const submitData = {
        ...form,
        weekend_price_nightly: form.weekend_price_nightly || null
      };

      if (isEditing) {
        await updateProperty(propertyId, submitData);
        toast.success('Property updated successfully');
      } else {
        const response = await createProperty(submitData);
        toast.success('Property created! Now add some photos.');
        navigate(`/app/host/properties/${response.data.id}`);
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (images.length === 0) {
      toast.error('Please add at least one photo before publishing');
      return;
    }

    try {
      await publishProperty(propertyId);
      toast.success('Property published successfully!');
      navigate('/app/host/properties');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish property');
    }
  };

  const getImageUrl = (image) => {
    const path = image.path_thumb || image.path_medium;
    if (!path) return '';
    return path.startsWith('http') ? path : `${API_URL}${path}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="property-form">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {isEditing ? 'Edit Property' : 'Add New Property'}
        </h1>
        <p className="text-slate-500 mt-1">
          {isEditing ? 'Update your property details' : 'Create a new vacation rental listing'}
        </p>
      </div>

      {/* Listing Readiness Checklist - Show only when editing */}
      {isEditing && (
        <ListingReadinessChecklist form={form} images={images} hostProfile={hostProfile} />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City *</Label>
                <Select value={form.city_id} onValueChange={(v) => handleChange('city_id', v)}>
                  <SelectTrigger data-testid="property-city">
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city.id} value={city.id}>
                        {city.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_type">Property Type *</Label>
                <Select value={form.property_type} onValueChange={(v) => handleChange('property_type', v)}>
                  <SelectTrigger data-testid="property-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PROPERTY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Beautiful beachfront condo with ocean views"
                data-testid="property-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe your property, its features, and what makes it special..."
                rows={5}
                data-testid="property-description"
              />
            </div>
          </CardContent>
        </Card>

        {/* Property Details */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Property Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="beds">Bedrooms</Label>
                <Input
                  id="beds"
                  type="number"
                  min="1"
                  max="20"
                  value={form.beds}
                  onChange={(e) => handleChange('beds', parseInt(e.target.value))}
                  data-testid="property-beds"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="baths">Bathrooms</Label>
                <Input
                  id="baths"
                  type="number"
                  min="0.5"
                  max="10"
                  step="0.5"
                  value={form.baths}
                  onChange={(e) => handleChange('baths', parseFloat(e.target.value))}
                  data-testid="property-baths"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_guests">Max Guests</Label>
                <Input
                  id="max_guests"
                  type="number"
                  min="1"
                  max="50"
                  value={form.max_guests}
                  onChange={(e) => handleChange('max_guests', parseInt(e.target.value))}
                  data-testid="property-guests"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pricing - Enhanced */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Pricing
            </CardTitle>
            <CardDescription>Set your nightly rates and optional discounts</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Base Rates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="base_price">Weekday Rate ($)</Label>
                <p className="text-xs text-slate-500">Sun-Thu nights</p>
                <Input
                  id="base_price"
                  type="number"
                  min="1"
                  value={form.base_price_nightly / 100}
                  onChange={(e) => handleChange('base_price_nightly', Math.round(parseFloat(e.target.value) * 100))}
                  data-testid="property-price"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="weekend_price">Weekend Rate ($)</Label>
                <p className="text-xs text-slate-500">Fri & Sat nights (optional)</p>
                <Input
                  id="weekend_price"
                  type="number"
                  min="0"
                  placeholder="Same as weekday"
                  value={form.weekend_price_nightly ? form.weekend_price_nightly / 100 : ''}
                  onChange={(e) => {
                    const val = e.target.value ? Math.round(parseFloat(e.target.value) * 100) : null;
                    handleChange('weekend_price_nightly', val);
                  }}
                  data-testid="property-weekend-price"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cleaning_fee">Cleaning Fee ($)</Label>
                <Input
                  id="cleaning_fee"
                  type="number"
                  min="0"
                  value={form.cleaning_fee / 100}
                  onChange={(e) => handleChange('cleaning_fee', Math.round(parseFloat(e.target.value) * 100))}
                  data-testid="property-cleaning-fee"
                />
              </div>
            </div>

            {/* Discounts Section */}
            <div className="border-t border-slate-100 pt-6">
              <h4 className="font-medium text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Length-of-Stay Discounts
              </h4>
              
              {/* Weekly Discount */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="weekly_discount" className="cursor-pointer">Weekly Discount</Label>
                    <span className="text-xs text-slate-500">(7+ nights)</span>
                  </div>
                  <p className="text-sm text-slate-500">Encourage longer stays</p>
                </div>
                <div className="flex items-center gap-3">
                  {form.weekly_discount_enabled && (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        className="w-16 h-8 text-center"
                        value={form.weekly_discount_percent}
                        onChange={(e) => handleChange('weekly_discount_percent', parseInt(e.target.value) || 0)}
                        data-testid="weekly-discount-percent"
                      />
                      <span className="text-sm text-slate-500">%</span>
                    </div>
                  )}
                  <Switch
                    id="weekly_discount"
                    checked={form.weekly_discount_enabled}
                    onCheckedChange={(v) => handleChange('weekly_discount_enabled', v)}
                    data-testid="weekly-discount-toggle"
                  />
                </div>
              </div>

              {/* Monthly Discount */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="monthly_discount" className="cursor-pointer">Monthly Discount</Label>
                    <span className="text-xs text-slate-500">(28+ nights)</span>
                  </div>
                  <p className="text-sm text-slate-500">Attract extended stays</p>
                </div>
                <div className="flex items-center gap-3">
                  {form.monthly_discount_enabled && (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min="1"
                        max="50"
                        className="w-16 h-8 text-center"
                        value={form.monthly_discount_percent}
                        onChange={(e) => handleChange('monthly_discount_percent', parseInt(e.target.value) || 0)}
                        data-testid="monthly-discount-percent"
                      />
                      <span className="text-sm text-slate-500">%</span>
                    </div>
                  )}
                  <Switch
                    id="monthly_discount"
                    checked={form.monthly_discount_enabled}
                    onCheckedChange={(v) => handleChange('monthly_discount_enabled', v)}
                    data-testid="monthly-discount-toggle"
                  />
                </div>
              </div>
            </div>

            {/* Stay Length */}
            <div className="border-t border-slate-100 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="min_nights">Minimum Nights</Label>
                  <Input
                    id="min_nights"
                    type="number"
                    min="1"
                    value={form.min_nights}
                    onChange={(e) => handleChange('min_nights', parseInt(e.target.value))}
                    data-testid="property-min-nights"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_nights">Maximum Nights</Label>
                  <Input
                    id="max_nights"
                    type="number"
                    min="1"
                    value={form.max_nights}
                    onChange={(e) => handleChange('max_nights', parseInt(e.target.value))}
                    data-testid="property-max-nights"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Safety Disclosures */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Safety & Privacy Disclosures
            </CardTitle>
            <CardDescription>
              Required disclosure of monitoring devices. This will be shown to guests.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
              <Checkbox
                id="has_security_cameras"
                checked={form.has_security_cameras}
                onCheckedChange={(v) => handleChange('has_security_cameras', v)}
                data-testid="has-security-cameras"
              />
              <div>
                <Label htmlFor="has_security_cameras" className="cursor-pointer font-medium">
                  Exterior Security Cameras
                </Label>
                <p className="text-sm text-slate-500">
                  Security cameras are present on the property exterior (driveway, entrance, etc.)
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
              <Checkbox
                id="has_noise_monitors"
                checked={form.has_noise_monitors}
                onCheckedChange={(v) => handleChange('has_noise_monitors', v)}
                data-testid="has-noise-monitors"
              />
              <div>
                <Label htmlFor="has_noise_monitors" className="cursor-pointer font-medium">
                  Noise Monitoring Devices
                </Label>
                <p className="text-sm text-slate-500">
                  Noise monitoring devices are present to detect loud sounds (no audio recording)
                </p>
              </div>
            </div>

            <p className="text-xs text-slate-400">
              Note: Interior cameras and audio recording devices are prohibited in rental properties.
            </p>
          </CardContent>
        </Card>

        {/* Booking Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Booking Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Instant Book</Label>
                <p className="text-sm text-slate-500">Allow guests to book instantly without approval</p>
              </div>
              <Switch
                checked={form.instant_book_enabled}
                onCheckedChange={(v) => handleChange('instant_book_enabled', v)}
                data-testid="property-instant-book"
              />
            </div>
            <div className="space-y-2">
              <Label>Cancellation Policy</Label>
              <Select value={form.cancellation_policy} onValueChange={(v) => handleChange('cancellation_policy', v)}>
                <SelectTrigger data-testid="property-cancellation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CANCELLATION_POLICIES.map((policy) => (
                    <SelectItem key={policy.value} value={policy.value}>
                      {policy.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Amenities */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Amenities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {amenities.map((amenity) => (
                <div key={amenity.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={amenity.id}
                    checked={form.amenities.includes(amenity.id)}
                    onCheckedChange={() => handleAmenityToggle(amenity.id)}
                    data-testid={`amenity-${amenity.id}`}
                  />
                  <label htmlFor={amenity.id} className="text-sm cursor-pointer">
                    {amenity.label}
                  </label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Address (Private) */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Property Address (Private)</CardTitle>
            <p className="text-sm text-slate-500">This address is only shared with confirmed guests</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="address_line1">Address Line 1</Label>
              <Input
                id="address_line1"
                value={form.address_line1}
                onChange={(e) => handleChange('address_line1', e.target.value)}
                placeholder="123 Beach Drive"
                data-testid="property-address1"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address_line2">Address Line 2</Label>
                <Input
                  id="address_line2"
                  value={form.address_line2}
                  onChange={(e) => handleChange('address_line2', e.target.value)}
                  placeholder="Unit 5"
                  data-testid="property-address2"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postal_code">Postal Code</Label>
                <Input
                  id="postal_code"
                  value={form.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  placeholder="33901"
                  data-testid="property-postal"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Images (only for editing) */}
        {isEditing && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                Photos
              </CardTitle>
              <CardDescription>Add photos to showcase your property. High-quality photos attract more bookings.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {images.map((image) => (
                  <div key={image.id} className="relative group aspect-property rounded-lg overflow-hidden bg-slate-100">
                    <img 
                      src={getImageUrl(image)}
                      alt="Property"
                      className="w-full h-full object-cover"
                    />
                    {image.is_cover && (
                      <span className="absolute top-2 left-2 bg-primary text-white text-xs px-2 py-1 rounded">
                        Cover
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => handleImageDelete(image.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                
                {/* Upload Button */}
                <label className="aspect-property rounded-lg border-2 border-dashed border-slate-300 hover:border-primary cursor-pointer flex flex-col items-center justify-center text-slate-500 hover:text-primary transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                  {uploading ? (
                    <Loader2 className="w-8 h-8 animate-spin" />
                  ) : (
                    <>
                      <Upload className="w-8 h-8 mb-2" />
                      <span className="text-sm">Upload Photos</span>
                    </>
                  )}
                </label>
              </div>

              {/* Hire a Photographer Tile */}
              <div className="mt-6 p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900">Need professional photos?</h4>
                    <p className="text-sm text-slate-600 mt-1">
                      Professional photography can increase bookings by up to 40%. 
                      Find a local photographer to showcase your property.
                    </p>
                    <a
                      href="https://www.thumbtack.com/k/real-estate-photographers/near-me/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary font-medium mt-2 hover:underline"
                      data-testid="hire-photographer-link"
                    >
                      Find photographers near you
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <Button type="button" variant="outline" onClick={() => navigate('/app/host/properties')}>
            Cancel
          </Button>
          <div className="flex gap-2">
            <Button type="submit" disabled={saving} data-testid="save-property-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isEditing ? 'Save Changes' : 'Create Property'}
            </Button>
            {isEditing && (
              <Button 
                type="button" 
                className="btn-accent" 
                onClick={handlePublish}
                data-testid="publish-property-btn"
              >
                Publish
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
